import api from "./api";

export type YoutubeOwnerConnectionStatus = {
  connected: boolean;
  channelName?: string | null;
  channelId?: string | null;
  channelImageUrl?: string | null;
  requiresReconnect: boolean;
  tokenStatus?: string | null;
  lastSyncedAt?: string | null;
};

export type YoutubeAppNotification = {
  id: string;
  message: string;
  createdAt: string;
};

export const YOUTUBE_RECONNECT_PROMPT_SESSION_KEY = "app.youtubeReconnectPrompt.shown.v1";

/** Chamar a cada login bem-sucedido para que o prompt seja reavaliado na nova sessão do usuário. */
export const resetYoutubeReconnectPromptFlag = (): void => {
  try {
    sessionStorage.removeItem(YOUTUBE_RECONNECT_PROMPT_SESSION_KEY);
  } catch {
    // ignore
  }
};

let pendingYoutubeOAuthPopup: Window | null = null;

/** Guarda uma janela de popup já aberta por um gesto real do usuário (ex.: clique no Dashboard), pra ser reaproveitada pela página de destino após a navegação client-side. */
export const setPendingYoutubeOAuthPopup = (popup: Window | null): void => {
  pendingYoutubeOAuthPopup = popup;
};

/** Consome (e limpa) a janela de popup pendente, se houver. */
export const takePendingYoutubeOAuthPopup = (): Window | null => {
  const popup = pendingYoutubeOAuthPopup;
  pendingYoutubeOAuthPopup = null;
  return popup;
};

const YOUTUBE_NOTIFICATIONS_KEY = "app.notifications.youtube.v1";
const YOUTUBE_NOTIFICATIONS_LAST_SEEN_KEY = "app.notifications.youtube.lastSeenAt.v1";
const YOUTUBE_NOTIFICATIONS_EVENT = "youtube-notifications-updated";

const normalizeStatus = (raw: any): YoutubeOwnerConnectionStatus => {
  const source = raw?.data ?? raw ?? {};
  const tokenStatus = source?.tokenStatus ? String(source.tokenStatus) : null;
  const requiresReconnect =
    Boolean(source?.requiresReconnect) ||
    String(tokenStatus ?? "").trim().toUpperCase() === "RECONNECT_REQUIRED";

  return {
    connected: !!source?.connected,
    channelName: source?.channelName ? String(source.channelName) : null,
    channelId: source?.channelId ? String(source.channelId) : null,
    channelImageUrl: source?.channelImageUrl ? String(source.channelImageUrl) : null,
    requiresReconnect,
    tokenStatus,
    lastSyncedAt: source?.lastSyncedAt ? String(source.lastSyncedAt) : null,
  };
};

export const getYoutubeOwnerConnectionStatus = async (): Promise<YoutubeOwnerConnectionStatus> => {
  const response = await api.get("/app/lives/youtube/owner-connection/status");
  return normalizeStatus(response.data);
};

export const startYoutubeOwnerConnection = async (
  options?: { popup?: boolean }
): Promise<{ authorizationUrl?: string | null }> => {
  const browserReturnUrl = typeof window !== "undefined" ? window.location.href : undefined;
  const response = await api.post("/app/lives/youtube/owner-connection/start", {
    ...(browserReturnUrl ? { browserReturnUrl } : {}),
    ...(options?.popup ? { popup: true } : {}),
  });
  const payload = response.data?.data ?? response.data ?? {};
  return {
    authorizationUrl: payload?.authorizationUrl ? String(payload.authorizationUrl) : null,
  };
};

export const disconnectYoutubeOwnerConnection = async (): Promise<void> => {
  await api.post("/app/lives/youtube/owner-connection/disconnect");
};

/** Número internacional sem + (ex.: 555193939487). Sobrescreva com REACT_APP_SUPPORT_WHATSAPP. */
export const getSupportWhatsappE164 = (): string =>
  String(process.env.REACT_APP_SUPPORT_WHATSAPP || "555193939487").replace(/\D/g, "") || "555193939487";

export const buildSupportWhatsAppUrl = (message: string): string => {
  const phone = getSupportWhatsappE164();
  const text = String(message || "").trim();
  return `https://wa.me/${phone}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
};

const readYoutubeNotifications = (): YoutubeAppNotification[] => {
  try {
    const raw = localStorage.getItem(YOUTUBE_NOTIFICATIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item: any) => ({
        id: String(item?.id ?? ""),
        message: String(item?.message ?? "").trim(),
        createdAt: String(item?.createdAt ?? ""),
      }))
      .filter((item) => item.id && item.message);
  } catch {
    return [];
  }
};

const writeYoutubeNotifications = (rows: YoutubeAppNotification[]) => {
  try {
    localStorage.setItem(YOUTUBE_NOTIFICATIONS_KEY, JSON.stringify(rows.slice(0, 20)));
    window.dispatchEvent(new Event(YOUTUBE_NOTIFICATIONS_EVENT));
  } catch {
    // ignore localStorage errors
  }
};

export const listYoutubeNotifications = (): YoutubeAppNotification[] => readYoutubeNotifications();

/** Quantas notificações YouTube são mais recentes que a última vez que o usuário abriu o menu (badge). */
export const getYoutubeNotificationUnreadCount = (items: YoutubeAppNotification[]): number => {
  try {
    const lastSeenRaw = localStorage.getItem(YOUTUBE_NOTIFICATIONS_LAST_SEEN_KEY);
    if (!lastSeenRaw) {
      return items.length;
    }
    const lastSeenMs = Date.parse(lastSeenRaw);
    if (Number.isNaN(lastSeenMs)) {
      return items.length;
    }
    return items.filter((item) => {
      const createdMs = Date.parse(item.createdAt);
      return !Number.isNaN(createdMs) && createdMs > lastSeenMs;
    }).length;
  } catch {
    return items.length;
  }
};

/** Chamar ao abrir o menu de notificações para zerar o badge das notificações YouTube já exibidas. */
export const markYoutubeNotificationsSeen = (): void => {
  try {
    localStorage.setItem(YOUTUBE_NOTIFICATIONS_LAST_SEEN_KEY, new Date().toISOString());
    window.dispatchEvent(new Event(YOUTUBE_NOTIFICATIONS_EVENT));
  } catch {
    // ignore
  }
};

export const pushYoutubeNotification = (notification: { message: string }) => {
  const now = new Date().toISOString();
  const item: YoutubeAppNotification = {
    id: `youtube-${Date.now()}`,
    message: String(notification.message || "").trim(),
    createdAt: now,
  };
  const next = [item, ...readYoutubeNotifications()];
  writeYoutubeNotifications(next);
};

export const upsertYoutubeReconnectNotification = (message: string) => {
  const rows = readYoutubeNotifications();
  const stableId = "youtube-reconnect-required";
  const rest = rows.filter((row) => row.id !== stableId);
  const next: YoutubeAppNotification = {
    id: stableId,
    message: String(message || "").trim(),
    createdAt: new Date().toISOString(),
  };
  writeYoutubeNotifications([next, ...rest]);
};

export const clearYoutubeReconnectNotification = () => {
  const rows = readYoutubeNotifications().filter((row) => row.id !== "youtube-reconnect-required");
  writeYoutubeNotifications(rows);
};

export const youtubeNotificationsEventName = YOUTUBE_NOTIFICATIONS_EVENT;
